<!-- src/components/Schedule.vue -->
<template>
    <div class="schedule-container">
        <!-- 时间卡片 - 紧凑型 -->
        <div class="glass-card time-card">
            <span class="time-label">当前时间</span>
            <span class="time-value">{{ formattedTime }}</span>
        </div>

        <div class="progress-grid">
            <!-- 今日 -->
            <div class="glass-card progress-card">
                <div class="progress-header">
                    <div class="header-left">
                        <span class="progress-icon sun">☀️</span>
                        <span class="label">今日已过</span>
                    </div>
                    <span class="percentage-text">{{ Math.round(dayPercentage) }}%</span>
                </div>
                <a-progress
                    :percent="Math.round(dayPercentage) / 100"
                    :stroke-width="12"
                    :show-text="false"
                    :status="getStatus(dayPercentage)"
                    class="custom-progress"
                />
            </div>

            <!-- 本月 -->
            <div class="glass-card progress-card">
                <div class="progress-header">
                    <div class="header-left">
                        <span class="progress-icon moon">📅</span>
                        <span class="label">本月已过</span>
                    </div>
                    <span class="percentage-text">{{ Math.round(monthPercentage) }}%</span>
                </div>
                <a-progress
                    :percent="Math.round(monthPercentage) / 100"
                    :stroke-width="12"
                    :show-text="false"
                    :status="getStatus(monthPercentage)"
                    class="custom-progress"
                />
            </div>

            <!-- 本年 -->
            <div class="glass-card progress-card">
                <div class="progress-header">
                    <div class="header-left">
                        <span class="progress-icon earth">🌏</span>
                        <span class="label">本年已过</span>
                    </div>
                    <span class="percentage-text">{{ Math.round(yearPercentage) }}%</span>
                </div>
                <a-progress
                    :percent="Math.round(yearPercentage) / 100"
                    :stroke-width="12"
                    :show-text="false"
                    :status="getStatus(yearPercentage)"
                    class="custom-progress"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';

export default defineComponent({
    name: "Schedule",
    setup() {
        const currentTime = ref(new Date());
        let timer = null;

        const formattedTime = computed(() => {
            return currentTime.value.toLocaleString('zh-CN', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        });

        const dayPercentage = computed(() => {
            const now = currentTime.value;
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const secondsInDay = 86400;
            const elapsedSeconds = (now - startOfDay) / 1000;
            return Math.min(100, Math.max(0, (elapsedSeconds / secondsInDay) * 100));
        });

        const monthPercentage = computed(() => {
            const now = currentTime.value;
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            const totalSeconds = (startOfNextMonth - startOfMonth) / 1000;
            const elapsedSeconds = (now - startOfMonth) / 1000;
            return Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100));
        });

        const yearPercentage = computed(() => {
            const now = currentTime.value;
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            const startOfNextYear = new Date(now.getFullYear() + 1, 0, 1);
            const totalSeconds = (startOfNextYear - startOfYear) / 1000;
            const elapsedSeconds = (now - startOfYear) / 1000;
            return Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100));
        });

        const updateTime = () => {
            currentTime.value = new Date();
        };

        const getStatus = (percentage) => {
            if (percentage < 30) return "success"; 
            if (percentage < 80) return "normal"; 
            return "warning";
        };

        onMounted(() => {
            updateTime();
            timer = setInterval(updateTime, 1000);
        });

        onBeforeUnmount(() => {
            clearInterval(timer);
        });

        return {
            formattedTime,
            dayPercentage,
            monthPercentage,
            yearPercentage,
            getStatus,
        };
    }
});
</script>

<style scoped>
.schedule-container {
    padding: 10px 20px;
    width: 100%;
    max-width: 520px; /* 限制最大宽度，防止卡片过长 */
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* 统一玻璃卡片样式 */
.glass-card {
    background: linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.35) 100%);
    border: 1px solid rgba(255,255,255,0.5);
    border-radius: 16px; /* 圆角适中 */
    padding: 16px 20px; /* 减小内边距 */
    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    transition: all 0.3s ease;
}

.glass-card:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.45) 100%);
    box-shadow: 0 8px 30px rgba(0,0,0,0.08);
}

/* 时间卡片 */
.time-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 24px;
}

.time-label {
    font-size: 14px;
    color: rgba(0,0,0,0.5);
    font-weight: 500;
}

.time-value {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    /* font-family: 'Inter', sans-serif; 使用全局字体 */
    font-variant-numeric: tabular-nums;
    letter-spacing: 0px;
}

/* 进度条区域 */
.progress-grid {
    display: flex;
    flex-direction: column;
    gap: 20px; /* 从 12px 增加到 20px，拉大间距 */
}

.progress-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px; /* 减小标题和进度条的间距 */
}

.header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.progress-icon {
    font-size: 16px;
}

.label {
    font-size: 14px;
    font-weight: 600;
    color: #444;
}

.percentage-text {
    font-size: 14px;
    font-weight: 700;
    color: #165DFF;
}

/* 覆盖 Arco Progress 样式 */
:deep(.custom-progress .arco-progress-line-track) {
    background-color: rgba(0,0,0,0.05);
    border-radius: 6px;
    height: 12px !important; /* 进度条变细一点，更精致 */
}

:deep(.custom-progress .arco-progress-line-bar) {
    border-radius: 6px;
    height: 12px !important;
}
</style>
